import { url, required, maxLength } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { TextField } from 'braid-design-system';
import React, {
  type ChangeEvent,
  type FocusEvent,
  forwardRef,
  type Ref,
  useImperativeHandle,
  useState,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { useConfig } from 'src/app/shared/hooks/useConfig';
import {
  validateField,
  type ValidateFieldResult,
} from 'src/app/utils/validation';
import { actionUpdateApplicationFormUrl } from 'src/app/v1/state/modules/formData/applicationFormUrl';
import type { AppState } from 'src/types/appState';
import type { ApplicationUrlRef } from 'src/types/formFields';

import translations from './.vocab';

export const ApplicationUrl = forwardRef((_, ref: Ref<ApplicationUrlRef>) => {
  const { applicationFormUrl } = useSelector(
    (state: AppState) => ({
      applicationFormUrl: state.formData.applicationFormUrl,
    }),
    shallowEqual,
  );
  const dispatch = useDispatch();

  const { t } = useTranslations(translations);
  const { getLanguage } = useConfig();

  const [validationMessage, setValidationMessage] =
    useState<ValidateFieldResult>(null);
  const [inputValue, setInputValue] = useState(applicationFormUrl);

  const labelName = t('application-url-label');

  useImperativeHandle(ref, () => ({
    handleValidation: (skipIfNull = false) =>
      handleInputValidation(inputValue, skipIfNull),
  }));

  const handleInputValidation = (
    value: string,
    skipIfNull: boolean = false,
  ) => {
    const validationResult = validateField(
      {
        field: 'applicationUrl',
        validators: [maxLength, required, url],
        validatorProps: {
          max: 500,
          requiredLabel: labelName,
        },
        value,
      },
      skipIfNull,
      getLanguage(),
    );
    setValidationMessage(validationResult);
    return validationResult;
  };

  const handleOnBlur = (event: FocusEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const errorMessage = handleInputValidation(value);

    if (!errorMessage) {
      dispatch(actionUpdateApplicationFormUrl(value));
    }
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);
    handleInputValidation(value);
  };

  return (
    <TextField
      id="ApplicationUrlTextField"
      label={labelName}
      value={inputValue}
      onBlur={handleOnBlur}
      onChange={handleOnChange}
      tone={!validationMessage ? 'neutral' : 'critical'}
      message={validationMessage}
      reserveMessageSpace
    />
  );
});
